package uz.mint.frontend.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import uz.mint.frontend.i18n.Locale
import uz.mint.frontend.i18n.PrivacyPolicy
import uz.mint.frontend.i18n.PublicOffer
import uz.mint.frontend.i18n.stringsFor

/**
 * The landing page, built from the *Mint Landing.dc.html* design.
 *
 * Each locale gets its own `@Page` entry point (`/`, `/uz/`, `/en/`) so static export produces
 * a separately-indexable HTML file per language; they all render through here.
 *
 * The catalogue is **no longer hoisted here**. The hero's finder searches and the tariff grid
 * pages, and both are server-side now, so the two sections ask genuinely different questions and
 * each owns its own request. What is still hoisted is what has to be: the checkout dialog and the
 * legal overlay, both opened from a section but rendered above the whole page.
 */
@Composable
fun LandingPage(locale: Locale) {
    val strings = stringsFor(locale)
    var legal by remember { mutableStateOf<LegalDoc?>(null) }

    Column(Modifier.fillMaxWidth()) {
        TopBar(strings)
        NavHeader(locale, strings)

        // Nothing on this page opens checkout any more, and that is the point of the rework: both
        // the hero's finder and the destination grid answer "where are you going" and hand off to
        // the catalogue by link. Choosing a package happens there, where the customer can see all
        // of them — the dialog lives on `/plans`.
        Hero(locale, strings)
        Stats(strings)
        Tariffs(locale, strings)
        Advantages(strings)
        ClosingCta(strings)

        Footer(
            locale = locale,
            strings = strings,
        )
    }

    when (legal) {
        LegalDoc.OFFER -> LegalOverlay(strings.legalOffer, PublicOffer, strings) { legal = null }
        LegalDoc.PRIVACY -> LegalOverlay(strings.legalPrivacy, PrivacyPolicy, strings) { legal = null }
        null -> Unit
    }
}
